import * as React from "react";
import { cn } from "@/lib/utils";
import type { TagGroup } from "./types";
import { TAG_GROUP_COLORS } from "./types";

export interface GroupSelectorCardProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The tag group to display */
  group: TagGroup;
  /** Number of tags selected from this group */
  selectedCount: number;
  /** Whether the dropdown is currently open */
  isOpen: boolean;
  /** Whether to show the total tag count (default: true) */
  showTotalCount?: boolean;
  /**
   * Compact, content-width variant for the inline Issues-style filter row.
   * Default (false) keeps the full-width card used inside grids/modals.
   */
  compact?: boolean;
}

/**
 * Clickable card that triggers a dropdown for selecting tags from a group.
 * Shows icon, label, total tag count, and selected count indicator.
 *
 * Uses forwardRef to work properly with PopoverTrigger asChild.
 *
 * @example
 * ```tsx
 * <PopoverTrigger asChild>
 *   <GroupSelectorCard
 *     group={projectsGroup}
 *     selectedCount={2}
 *     isOpen={openDropdown === 'projects'}
 *   />
 * </PopoverTrigger>
 * ```
 */
export const GroupSelectorCard = React.forwardRef<HTMLButtonElement, GroupSelectorCardProps>(
  (
    { group, selectedCount, isOpen, showTotalCount = true, compact = false, className, ...props },
    ref
  ) => {
    const colors = TAG_GROUP_COLORS[group.color];
    const Icon = group.icon;
    const totalCount = group.tags.length;

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "relative flex items-center gap-2 rounded-lg border text-left transition-all",
          compact ? "h-8 min-w-0 px-3" : "w-full px-3 py-2.5",
          "hover:border-muted-foreground/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          isOpen && [colors.cardBorder, colors.cardBg, "shadow-sm"],
          !isOpen && "border-border bg-background hover:bg-muted/30",
          className
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        {...props}
      >
        <Icon
          className={cn(
            "shrink-0",
            compact ? "h-3.5 w-3.5" : "h-4 w-4",
            isOpen ? colors.chipText : "text-muted-foreground"
          )}
          aria-hidden="true"
        />
        <span className={cn("flex-1 truncate font-medium", compact ? "text-xs" : "text-sm")}>
          {group.label}
        </span>
        {showTotalCount && (
          <span className={cn("text-xs", isOpen ? colors.chipText : "text-muted-foreground")}>
            ({totalCount})
          </span>
        )}

        {selectedCount > 0 && (
          <span
            className={cn(
              "absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold text-white",
              colors.indicator
            )}
            aria-label={`${selectedCount} selected`}
          >
            {selectedCount}
          </span>
        )}
      </button>
    );
  }
);

GroupSelectorCard.displayName = "GroupSelectorCard";
